<template>
  <n-notification-provider>
  <n-message-provider>
    <n-dialog-provider>
    <div>
      <!-- 浏览器兼容性提醒 -->
      <compatibility-warning />
      <!-- 用户验证悬浮窗 -->
      <user-verification
        v-if="verificationState.showVerification"
        ref="verificationComponent"
      />
      
      <!-- 移动设备倒计时遮罩 -->
      <div v-if="showMobileOverlay" class="mobile-overlay">
        <div class="mobile-overlay-content">
          <img src="/images/logo.png" class="mobile-logo" alt="Logo" />
          <h2 class="mobile-title">小屏幕提示</h2>
          <p class="mobile-description">当前设备屏幕尺寸较小，可能影响您的浏览体验</p>
          <div class="countdown">{{ countdown }}秒后关闭</div>
        </div>
      </div>

      <!-- 常规内容 -->
      <router-view></router-view>

  </div>
  </n-dialog-provider>
</n-message-provider>
</n-notification-provider>
</template>
<script>
import UserVerification from './components/UserVerification.vue';
import verificationService from './services/verificationService';
import themeService from './services/themeService';
import CompatibilityWarning from './components/CompatibilityWarning.vue';
import { useTaskSSE } from './composables/useTaskSSE';
import FloatingNotification from './components/animations/FloatingNotification';
import {
  applyTransferredAuth,
  isAllowedTokenOrigin,
  isTokenTransferPayload
} from './services/tokenBridgeService';

export default {
  components: {
    UserVerification,
    CompatibilityWarning
  },
  data() {
    return {
      isMobile: false,
      windowWidth: window.innerWidth,
      verificationState: verificationService.getState(),
      showMobileOverlay: false,
      countdown: 3,
      countdownTimer: null,
      // SSE 相关
      sseInstance: null,
      // Token 传递处理标志
      tokenTransferProcessing: false,
      tokenTransferHandler: null
    };
  },

  created() {
    themeService.applyTheme('light');
    this.checkDevice();
    window.addEventListener('resize', this.checkDevice);

    // 监听来自其他项目的 token 传递
    this.setupTokenListener();

    // 初始化全局 SSE 连接管理
    this.initGlobalSSE();
  },

  watch: {
    '$route'(to) {
      // 路由变化时保存路径（除了登录页和首页）
      if (to.path !== '/login' && to.path !== '/' && to.path !== '/token-bridge') {
        localStorage.setItem('lastPath', to.path);
      }
    },
    isMobile(newValue) {
      // 当检测到小屏幕设备时，显示倒计时遮罩
      if (newValue) {
        this.showMobileOverlay = true;
        this.startCountdown();
      }
    }
  },

  methods: {
    startCountdown() {
      // 保持原有逻辑不变
      this.countdown = 3;
      
      if (this.countdownTimer) {
        clearInterval(this.countdownTimer);
      }
      
      this.countdownTimer = setInterval(() => {
        this.countdown -= 1;
        
        if (this.countdown <= 0) {
          clearInterval(this.countdownTimer);
          this.showMobileOverlay = false;
        }
      }, 1000);
    },
    
    checkDevice() {
      // 更新窗口宽度
      this.windowWidth = window.innerWidth;
      
      // 修改判断逻辑：区分小屏幕手机和平板设备
      const userAgent = navigator.userAgent.toLowerCase();
      
      // 检测是否为手机设备（排除平板）
      const phoneRegex = /android(?!.*tablet)|iphone|ipod|blackberry|iemobile|opera mini/i;
      const isPhone = phoneRegex.test(userAgent);
      
      // 判断屏幕尺寸（小于480px认为是小屏幕设备）
      const isSmallScreen = this.windowWidth < 480;
      
      // 只有当设备是手机且屏幕较小时才显示移动端提示
      const newMobileState = isPhone && isSmallScreen;
      
      // 只有当状态发生变化时才更新，避免重复触发倒计时
      if (this.isMobile !== newMobileState) {
        this.isMobile = newMobileState;
      }
    },

    // 监听来自其他项目的 token 传递
    setupTokenListener() {
      this.tokenTransferHandler = (event) => {
        if (!isAllowedTokenOrigin(event.origin) || !isTokenTransferPayload(event.data)) {
          return;
        }

        const receivedToken = event.data.token;
        const currentToken = localStorage.getItem('authToken');

        // token 一致则忽略
        if (receivedToken === currentToken) {
          return;
        }

        // 防止重复处理
        if (this.tokenTransferProcessing) {
          return;
        }

        this.tokenTransferProcessing = true;
        applyTransferredAuth(event.data);

        if (this.$route.path === '/token-bridge') {
          this.tokenTransferProcessing = false;
          return;
        }

        this.$router.push({ path: '/home' }).finally(() => {
          this.tokenTransferProcessing = false;
        });
      };

      window.addEventListener('message', this.tokenTransferHandler);
    },

    // 初始化全局 SSE 连接
    initGlobalSSE() {
      // 任务完成回调 - 直接显示全局通知
      const handleTaskCompleted = (notification) => {
        console.log('[App SSE] 任务完成通知:', notification);
        FloatingNotification.custom({
          text: `🎉 任务「${notification.taskName}」已完成！`,
          theme: 'success',
          shape: 'rounded',
          position: 'bottomRight',
          animation: 'floatUp',
          duration: 3000,
          playSound: true
        });
      };

      // 进度更新回调 - 使用持久化进度追踪器
      const handleProgressUpdate = (notification) => {
        console.log('[App SSE] 进度更新通知:', notification);
        FloatingNotification.progress(notification);
      };

      // 使用 SSE composable（autoConnect: false，等登录成功后手动连接）
      this.sseInstance = useTaskSSE({
        onTaskCompleted: handleTaskCompleted,
        onProgressUpdate: handleProgressUpdate,
        autoConnect: false
      });
    }
  },
  
  beforeUnmount() {
    // 移除事件监听器
    window.removeEventListener('resize', this.checkDevice);
    
    // 清除倒计时计时器
    if (this.countdownTimer) {
      clearInterval(this.countdownTimer);
    }

    if (this.tokenTransferHandler) {
      window.removeEventListener('message', this.tokenTransferHandler);
    }

    // 断开 SSE 连接
    if (this.sseInstance) {
      this.sseInstance.disconnect();
    }
  }
}
</script>

<style scoped>
.loading-container {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100vh;
  background-color: #f0f0f0;
}

.loading-content {
  display: flex;
  align-items: center;
}

.loading-image {
  width: 50px;
  height: 50px;
  animation: spin 2s linear infinite;
}

.loading-text {
  margin-left: 20px;
  font-size: 20px;
  color: #333;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* 移动设备倒计时遮罩样式 */
.mobile-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.7);
  z-index: 1000;
  display: flex;
  justify-content: center;
  align-items: center;
}

.mobile-overlay-content {
  background-color: white;
  border-radius: 8px;
  padding: 20px;
  text-align: center;
  max-width: 80%;
}

.mobile-logo {
  width: 60px;
  height: 60px;
  margin-bottom: 15px;
}

.mobile-title {
  font-size: 20px;
  color: #333;
  margin-bottom: 10px;
}

.mobile-description {
  font-size: 14px;
  color: #666;
  line-height: 1.4;
  margin-bottom: 15px;
}

.countdown {
  font-size: 16px;
  font-weight: bold;
  color: #e74c3c;
}

/* 保留原有的其他样式 */
.qrcode-image {
  width: 200px;
  height: 200px;
  max-width: 80%;
  border: 1px solid #eee;
  border-radius: 4px;
}
</style>
